import { useEffect, useMemo, useState } from 'react';

import { EditControllerProps, useEditController } from 'react-admin';
import { useFormContext } from 'react-hook-form';

type ExternalRecord = Record<string, any>;

type UseNormalizeReferencesArgs = {
  referenceFields: string[];
  delay?: number;
  externalRecord?: ExternalRecord | null;
  controllerProps?: EditControllerProps;
};

export const useNormalizeReferences = ({
  referenceFields,
  delay = 300,
  externalRecord,
  controllerProps,
}: UseNormalizeReferencesArgs) => {
  const { record: controllerRecord, isFetching: controllerLoading } =
    useEditController(controllerProps);

  // если передан рекорд снаружи — используем его
  const record = externalRecord ?? controllerRecord;
  const isLoading = externalRecord ? false : controllerLoading;

  const { watch, getValues, reset } = useFormContext();
  const [ready, setReady] = useState(false);

  const watchedValues = watch(referenceFields);

  const isNormalized = (value: any) => {
    if (!value) {
      return true;
    }
    if (typeof value === 'string') {
      return true;
    }
    if (Array.isArray(value)) {
      return value.every((v) => typeof v === 'string');
    }
    return false;
  };

  const allNormalized = useMemo(
    () => referenceFields.every((_, i) => isNormalized(watchedValues?.[i])),
    [watchedValues, referenceFields],
  );

  useEffect(() => {
    if (isLoading || !record || ready) {
      return;
    }

    const timer = setTimeout(() => {
      const normalizedValues: Record<string, any> = {};

      referenceFields.forEach((field) => {
        const value = record[field];

        if (!value) {
          return;
        }

        if (Array.isArray(value)) {
          normalizedValues[field] = value.map((v) => (typeof v === 'string' ? v : v?.['@id']));
        } else if (typeof value === 'object' && value['@id']) {
          normalizedValues[field] = value['@id'];
        }
      });

      if (Object.keys(normalizedValues).length) {
        reset({ ...getValues(), ...normalizedValues });
      }

      setReady(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [record, isLoading, referenceFields, getValues, reset, delay]);

  return { ready: ready && allNormalized, allNormalized };
};
